<template>
  <div class="mb-2 flex justify-center">
    <LinkToCreateEdit :to="{ name: 'userCreate' }">
      Add Staff User
    </LinkToCreateEdit>
  </div>

  <div class="mb-4">
    <SearchInput v-model="searchRaw" />
  </div>

  <BaseInnerLoading v-if="isLoading" />

  <p v-else-if="isError" class="text-error">
    Failed to load users. Please try again.
  </p>

  <p v-else-if="data && users.length === 0">
    No users found.
  </p>

  <ul
    v-else-if="data && users.length > 0"
    class="divide-y divide-background-fg/8"
  >
    <template v-for="user in users" :key="user.id">
      <li>
        <RouterLink
          :to="{
            name: 'userEdit',
            params: {
              userId: user.id,
            },
          }"
        >
          <div
            class="flex items-center justify-between space-x-4 p-2 text-sm"
          >
            <div class="flex-1">
              <p class="text-lg font-semibold">
                {{ user.firstName }} {{ user.lastName }}
              </p>
              <p
                v-if="user.email"
                class="flex items-center text-sm text-background-fg/87"
              >
                <IconHeroiconsEnvelope class="mr-2" />

                {{ user.email }}
              </p>
              <p
                v-if="user.phone"
                class="flex items-center text-sm text-background-fg/87"
              >
                <IconHeroiconsPhone class="mr-2" />
                {{ user.phone }}
              </p>
            </div>

            <div class="flex flex-col">
              <span
                v-if="user.status === 'invited'"
                class="rounded-full bg-info px-2 py-1 text-background"
              >
                Invited
              </span>
              <span
                v-else-if="user.status === 'active'"
                class="rounded-full bg-positive px-2 py-1 text-background"
              >
                Active
              </span>
              <span
                v-else-if="user.status === 'inactive'"
                class="rounded-full bg-error px-2 py-1 text-background"
              >
                Inactive
              </span>
              <span
                v-else-if="user.status === 'expired'"
                class="rounded-full bg-error px-2 py-1 text-background"
              >
                Invite Expired
              </span>

              <span
                v-if="user.isOwner"
                class="rounded-full bg-info px-2 py-1 text-background"
              >
                Owner
              </span>
            </div>
          </div>
        </RouterLink>
      </li>
    </template>
  </ul>

  <div
    v-if="data && users.length > 0"
    class="mt-4 flex justify-center"
  >
    <BasePagination
      v-model="page"
      :page-count="pageCount"
    />
  </div>
</template>

<script setup lang="ts">
const searchRaw = ref<string>("")
const search = refDebounced(searchRaw, 750)

const { pagination, page, pageCount, resetPagination } =
  usePagination({
    limit: 10,
    count: computed(() => data.value?.count || 0),
  })

watch(
  () => [search],
  () => resetPagination(),
  { deep: true, immediate: true }
)

const query = reactiveComputed(() => ({
  ...pagination.value,
  search: search.value?.trim(),
}))

const { data, isLoading, isError } = useUsers(query)

const users = computed(() => data.value?.items ?? [])
</script>
